import React, { useCallback, useEffect, useRef, useState } from 'react';
import { financeApi, outcomeOf, shortDate, type DeathRequestItem } from './financeApi';

/**
 * Death reports waiting on Finance (ruling, Isaiah Cotto, 2026-10-22).
 *
 * A provider's "Report deceased" files a request with no effect. Finance sees it here: "Death reported by <provider> on
 * <date>". Confirm records the death through Finance's own report (residentLifecycle.reportDeceased): the resident then
 * leaves the Patient Advocates' unassigned queue, round-robin and digest; the status itself is not changed here. Dismiss
 * closes the request and changes nothing else. Shown only to a caller who may record a death here (the server's
 * `can.report_deceased`); for anyone else the shell draws nothing and asks nothing.
 */

/** The open requests' count for the sidebar badge; null until read (and for a caller who may not see them). */
export function useDeathRequestCount(enabled: boolean): [number | null, (n: number) => void] {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) { setCount(null); return undefined; }
    let live = true;
    (async () => {
      try {
        const r = await financeApi.deathRequests();
        if (live) setCount(r.success && r.data ? r.data.requests.length : null);
      } catch { if (live) setCount(null); }
    })();
    return () => { live = false; };
  }, [enabled]);
  return [count, setCount];
}

type Asking = { item: DeathRequestItem } | null;

export default function DeathReports({ onCount }: { onCount?: (n: number) => void }) {
  const [items, setItems] = useState<DeathRequestItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<{ id: string; message: string } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [asking, setAsking] = useState<Asking>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await financeApi.deathRequests();
      if (!r.success || !r.data) { setItems(null); setErr(outcomeOf(r)); return; }
      setItems(r.data.requests);
      if (onCount) onCount(r.data.requests.length);
    } catch { setItems(null); setErr('Could not reach the server.'); }
  }, [onCount]);
  useEffect(() => { load(); }, [load]);

  // The confirmation takes the focus, and gives it back to the button that opened it.
  useEffect(() => { if (asking) confirmRef.current?.focus(); }, [asking]);
  const close = () => {
    setAsking(null);
    const back = opener.current;
    window.setTimeout(() => back?.focus(), 0);
  };

  const act = async (item: DeathRequestItem, kind: 'confirm' | 'dismiss') => {
    setBusy(item.id);
    setRowErr(null);
    setDone(null);
    try {
      const r = kind === 'confirm' ? await financeApi.confirmDeathRequest(item.id) : await financeApi.dismissDeathRequest(item.id);
      if (!r.success) { setRowErr({ id: item.id, message: outcomeOf(r, { wrote: true }) }); return; }
      setDone(kind === 'confirm' ? `Death recorded for ${item.resident_name}.` : `Report for ${item.resident_name} dismissed.`);
      await load();
    } catch {
      setRowErr({ id: item.id, message: 'Could not reach the server. Nothing was recorded.' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="um-card" aria-label="Death reports waiting on Finance">
      <div className="um-card-head">
        <div>
          <h2>Death reports</h2>
          <p className="why">
            Providers report a death from the resident&apos;s row; nothing changes until Finance confirms it. Confirm records
            the death, and the resident leaves the Patient Advocates&apos; queues. Dismiss closes the report and changes nothing.
          </p>
        </div>
      </div>
      {done && <p className="um-notice" role="status" style={{ margin: 12 }}>{done}</p>}
      {err && (
        <div style={{ margin: 12 }}>
          <p className="um-notice stop" role="alert">{err}</p>
          <button type="button" className="um-open" onClick={() => load()}>Try again</button>
        </div>
      )}
      {!err && items === null && <p className="um-quiet" style={{ padding: 12 }}>Loading…</p>}
      {items && items.length === 0 && <p className="um-quiet" style={{ padding: 12 }}>No death reports are waiting.</p>}
      {items && items.length > 0 && (
        <ul className="fn-rows" aria-label="Death reports">
          {items.map((it) => (
            <li key={it.id}>
              <div className="fn-row" aria-label={`${it.resident_name}: death reported by ${it.requested_by_name} on ${shortDate(it.effective_date)}`}>
                <span className="who">{it.resident_name}</span>
                <span className="where">Death reported by {it.requested_by_name} on {shortDate(it.effective_date)}</span>
                <span className="state">Waiting on Finance · confirm or dismiss</span>
              </div>
              <div className="fn-sendback">
                <button
                  type="button"
                  className="um-open"
                  disabled={busy !== null}
                  onClick={(e) => { opener.current = e.currentTarget; setAsking({ item: it }); }}
                >
                  {busy === it.id ? 'Recording…' : `Confirm death of ${it.resident_name}`}
                </button>{' '}
                <button type="button" className="um-open" disabled={busy !== null} onClick={() => act(it, 'dismiss')}>
                  {`Dismiss report for ${it.resident_name}`}
                </button>
                {rowErr && rowErr.id === it.id && <p className="um-notice stop" role="alert">{rowErr.message}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {asking && (
        <div
          ref={dialogRef}
          className="um-card fn-death-confirm"
          role="dialog"
          aria-modal="true"
          aria-label={`Confirm the death of ${asking.item.resident_name}`}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } }}
          style={{ margin: 12, padding: 12 }}
        >
          <p>
            Record the death of <strong>{asking.item.resident_name}</strong> on {shortDate(asking.item.effective_date)}, as
            reported by {asking.item.requested_by_name}?
          </p>
          <p className="um-quiet">
            This records it through Finance&apos;s report. The resident leaves the Patient Advocates&apos; unassigned queue,
            round-robin and digest. The resident&apos;s status itself is not changed here.
          </p>
          <div className="fn-sendback">
            <button
              ref={confirmRef}
              type="button"
              className="um-open"
              onClick={() => { const it = asking.item; close(); void act(it, 'confirm'); }}
            >
              Confirm death
            </button>{' '}
            <button type="button" className="um-open" onClick={close}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
